/**
 * Scope Microsoft centralizzati + costruzione URL OAuth con PKCE (S256).
 *
 * Unico punto in cui si definisce la mappa servizio→scope e si costruisce
 * l'authorize URL di Microsoft. Prima questa logica era duplicata e hardcoded
 * in MicrosoftAccountSection e MicrosoftWorkspaceSettings.
 *
 * PKCE: il verifier viene generato dal frontend e salvato in sessionStorage
 * PRIMA del redirect; il challenge (base64url(sha256(verifier))) viene incluso
 * nell'authorize URL. Dopo il redirect, il callback recupera il verifier con
 * getStoredPkceVerifier() e lo passa alla edge microsoft-auth-token, che lo
 * include nella token request.
 */

export type MicrosoftService = 'onedrive' | 'calendar' | 'teams' | 'mail' | 'contacts';

/**
 * Scope per le trascrizioni delle riunioni Teams.
 * NON entra negli scope del connect utente standard: richiede il consenso
 * dell'amministratore del tenant (admin consent). Dopo l'admin consent, il
 * consenso incrementale fa sì che i token utente lo includano al prossimo
 * refresh/connect.
 */
export const TEAMS_TRANSCRIPTS_SCOPE = 'OnlineMeetingTranscript.Read.All';

// Scope OIDC + base sempre richiesti (identità, email, refresh token).
export const MICROSOFT_BASE_SCOPES = ['openid', 'email', 'profile', 'User.Read', 'offline_access'];

// Scope specifici per servizio F1.
export const MICROSOFT_SERVICE_SCOPES: Record<MicrosoftService, string[]> = {
  onedrive: ['Files.ReadWrite', 'Files.ReadWrite.All'],
  calendar: ['Calendars.ReadWrite'],
  teams: ['OnlineMeetings.Read'], // risoluzione meeting da joinUrl; il link Teams si crea con lo scope calendar
  mail: ['Mail.ReadWrite', 'Mail.Send', 'MailboxSettings.Read'], // Posta Outlook (lettura/scrittura, invio, master categories)
  contacts: ['Contacts.ReadWrite'], // Contatti Outlook (import/export bidirezionale con i clienti CRM)
};

// Scope base OIDC che Microsoft a volte NON riporta nella risposta granted:
// vanno ignorati nel confronto getMissingScopes per non generare falsi "mancanti".
const OIDC_BASE_SCOPES = ['openid', 'email', 'profile', 'offline_access'];

const PKCE_VERIFIER_STORAGE_KEY = 'ms_pkce_verifier';

/**
 * Union di base + scope dei servizi richiesti, deduplicati con ordine stabile
 * (prima i base nell'ordine dichiarato, poi i servizi nell'ordine dell'enum).
 */
export function scopesForServices(services: MicrosoftService[]): string[] {
  const ordered: string[] = [...MICROSOFT_BASE_SCOPES];

  const serviceOrder: MicrosoftService[] = ['onedrive', 'calendar', 'teams', 'mail', 'contacts'];
  const requested = new Set(services);
  for (const service of serviceOrder) {
    if (requested.has(service)) {
      ordered.push(...MICROSOFT_SERVICE_SCOPES[service]);
    }
  }

  // Dedup preservando l'ordine.
  const seen = new Set<string>();
  const result: string[] = [];
  for (const scope of ordered) {
    if (!seen.has(scope)) {
      seen.add(scope);
      result.push(scope);
    }
  }
  return result;
}

// --- PKCE helpers (WebCrypto) ---

/** base64url senza padding di un ArrayBuffer/Uint8Array. */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Genera un code_verifier PKCE valido: 32 byte random → base64url (43 char),
 * composto solo da caratteri unreserved [A-Za-z0-9-_], nel range 43-128.
 */
function generatePkceVerifier(): string {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  return base64UrlEncode(randomBytes);
}

/** code_challenge = base64url(SHA-256(verifier)) senza padding. */
async function computePkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Costruisce l'authorize URL di Microsoft con PKCE (S256).
 * Genera il verifier, lo salva in sessionStorage PRIMA del redirect e include
 * il challenge nell'URL. Legge client id / tenant / redirect da env Vite,
 * con la stessa logica dei componenti attuali.
 */
export async function buildMicrosoftAuthUrl(opts: {
  services: MicrosoftService[];
  redirectTo?: string;
  prompt?: 'consent' | 'select_account consent';
}): Promise<string> {
  const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
  const tenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID || 'common';

  if (!clientId) {
    throw new Error('Microsoft Client ID non configurato. Contatta l\'amministratore.');
  }

  const redirectUri =
    import.meta.env.VITE_MICROSOFT_REDIRECT_URI ||
    `${window.location.origin}/digital-microsoft-connect`;

  const scopes = scopesForServices(opts.services);

  const state = encodeURIComponent(
    JSON.stringify({ redirectTo: opts.redirectTo ?? '/' })
  );

  // PKCE: genera verifier, salvalo PRIMA del redirect, calcola il challenge.
  const verifier = generatePkceVerifier();
  sessionStorage.setItem(PKCE_VERIFIER_STORAGE_KEY, verifier);
  const challenge = await computePkceChallenge(verifier);

  const oauthUrl = new URL(`https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`);
  oauthUrl.searchParams.append('client_id', clientId);
  oauthUrl.searchParams.append('response_type', 'code');
  oauthUrl.searchParams.append('redirect_uri', redirectUri);
  oauthUrl.searchParams.append('scope', scopes.join(' '));
  oauthUrl.searchParams.append('response_mode', 'query');
  oauthUrl.searchParams.append('state', state);
  if (opts.prompt) {
    oauthUrl.searchParams.append('prompt', opts.prompt);
  }
  oauthUrl.searchParams.append('code_challenge', challenge);
  oauthUrl.searchParams.append('code_challenge_method', 'S256');

  return oauthUrl.toString();
}

/** Legge e RIMUOVE il verifier PKCE da sessionStorage (uso singolo). */
export function getStoredPkceVerifier(): string | null {
  try {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_STORAGE_KEY);
    if (verifier) {
      sessionStorage.removeItem(PKCE_VERIFIER_STORAGE_KEY);
    }
    return verifier;
  } catch {
    return null;
  }
}

/**
 * Restituisce gli scope richiesti dai servizi indicati che NON risultano tra
 * quelli concessi. Confronto case-insensitive; ignora gli scope base OIDC che
 * Microsoft a volte non riporta. Serve alla UI per mostrare "Autorizzazioni da
 * aggiornare".
 */
export function getMissingScopes(
  grantedScopes: string[] | null | undefined,
  services: MicrosoftService[]
): string[] {
  const granted = new Set((grantedScopes ?? []).map((s) => s.toLowerCase()));
  const ignore = new Set(OIDC_BASE_SCOPES.map((s) => s.toLowerCase()));

  const required = scopesForServices(services);
  const missing: string[] = [];
  for (const scope of required) {
    const lower = scope.toLowerCase();
    if (ignore.has(lower)) continue;
    if (!granted.has(lower)) {
      missing.push(scope);
    }
  }
  return missing;
}

/**
 * Costruisce l'URL di admin consent Microsoft per il tenant del cliente.
 * Usato per concedere, a livello di amministratore del tenant, gli scope che
 * non possono essere approvati dal singolo utente (es. le trascrizioni Teams
 * OnlineMeetingTranscript.Read.All). Il tenant è un dominio o un tenant id; MAI
 * "common". Dopo l'admin consent i token utente includono lo scope al prossimo
 * refresh/connect (consenso incrementale).
 */
export function buildAdminConsentUrl(tenantIdOrDomain: string): string {
  const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
  if (!clientId) {
    throw new Error('Microsoft Client ID non configurato. Contatta l\'amministratore.');
  }

  const tenant = (tenantIdOrDomain || '').trim();
  if (!tenant) {
    throw new Error('Inserisci il dominio o il Tenant ID del tuo Microsoft 365.');
  }

  const url = new URL(
    `https://login.microsoftonline.com/${encodeURIComponent(tenant)}/v2.0/adminconsent`
  );
  url.searchParams.append('client_id', clientId);
  url.searchParams.append('scope', 'https://graph.microsoft.com/.default');
  return url.toString();
}
